<template>
  <div class="FacetDropdown" :class="{ 'is-open': isOpen }">
    <button v-if="['price', 'select'].includes(type)" class="FacetDropdown__toggle" type="button" @click="toggle">
      <span v-if="type === 'price'" class="text-primary-A900 text-base font-bold">{{ $t('price') }}</span>
      <span v-else class="text-primary-A900 text-base font-bold">{{ label }}</span>

      <svg-icon
        :name="isOpen ? 'minus' : 'plus'"
        class="text-primary-A900 ml-auto h-5 w-5 FacetDropdown__toggle-caret"
      />
    </button>

    <div v-if="isToggleProps(props)" class="w-full py-4 flex items-center justify-between">
      <span class="text-primary-A900 text-base font-bold">
        {{ label }}
      </span>

      <ToggleInput
        :model-value="!!props.value"
        :true-value="true"
        :false-value="false"
        @update:model-value="(e: boolean) => updateFacet(e ? props.options : null)"
      />
    </div>

    <template v-if="isOpen">
      <div v-if="isSelectProps(props)">
        <GenericMultiselect
          :model-value="props.value"
          :options="localizedOptions"
          :label="label"
          identified-by="value"
          immediate
          :reversed="true"
          @input="updateFacet"
        />
      </div>
      <PriceFacet
        v-if="isPriceProps(props)"
        :value="[props?.value?.[0] ?? 0, props?.value?.[1] ?? 0]"
        :options="[props.options[0], props.options[props.options.length - 1]]"
        @input="updateFacet"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import type { AggregationOption } from '@robustastudio/e-commerce/common';
import { isPriceProps, isSelectProps, isToggleProps, type AllFacets } from '~/types/facets';

type localization = {
  isLocalized: boolean;
};
const props = defineProps<AllFacets & localization>();

// const isToggleProps = (props: Props): props is IToggleProps => props.type === 'toggle';

const emit = defineEmits<{
  (e: 'input', value: typeof props.value): void;
}>();

// const currencyCode = inject(CONFIG_STORE_CURRENCY);
const { isOpen, toggle } = useDropdown();
const localizedOptions = computed(() => {
  if (props.code === 'ac_horse_power') {
    if (isSelectProps(props)) {
      return props.options.map(i => {
        return {
          ...i,
          label: `${i.label} ${$t('horsePower')}`,
        };
      });
    }
  }
  return props.options;
});

const { t: $t } = useI18n({
  useScope: 'local',
});

// const selectedLabel = computed(() => {
//   if (isSelectProps(props)) {
//     return props.value?.map(curr => curr?.label).join(',');
//   }

//   // Skip including price if it already has the value of the existing min/max range.
//   if (isPriceProps(props) && props.value.length) {
//     return `${props.value[0]} ${currencyCode?.value} - ${props.value[1]} ${currencyCode?.value} `;
//   }

//   return '';
// });

/** * called when a new facet option is selected / deselected * handles emitting the new values to the parent component
(category page) */
function updateFacet(newValue: AggregationOption | number[] | null) {
  emit('input', newValue);
}
</script>

<style lang="postcss" scoped>
.FacetDropdown {
  @apply relative;

  &__toggle {
    @apply w-full flex items-center  rounded-6sm py-4 text-sm;

    &:focus {
      @apply outline-none;
    }

    &-caret {
      transition: transform 0.2s ease-in-out;
    }
  }

  &.is-open {
    .FacetDropdown__toggle-caret {
      transform: rotate(180deg);
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
<i18n>
{
  "en": {
    "select": "Select ...",
    "price": "PRICE",
    "horsePower": "HP",
  },
  "ar": {
    "select": "أختر ...",
    "price": "السعر",
    "horsePower": "حصان"
  }
}
</i18n>
